// src/pages/BCCManager/index.jsx

import React, { useState, useEffect } from "react";
import axios from "axios";
import UserTable from "./components/UserTable";
import AddUserModal from "./components/AddUserModal";
import {AES, enc} from "crypto-js";
import {useUserAuth} from "../../../context/UserAuthContext";


export default function BCCManager() {
    const [users, setUsers]               = useState([]);
    const [isModalOpen, setIsModalOpen]   = useState(false);
    const [editingUser, setEditingUser]   = useState(null);
    const [notification, setNotification] = useState(null);
    const [isLoading, setIsLoading]       = useState(false);
    const [error, setError]               = useState(null);
    let {Token} = useUserAuth();
    const secretKey = "cromptonssecretkey";
    const decryptedValue = AES.decrypt(Token, secretKey).toString(enc.Utf8);

    const API_BASE = process.env.REACT_APP_CROMPTON_SENZE_FEEDBACK;

    // ─── Toast color map ──────────────────────────────────────────────────────────
    const toastClass = {
        success: "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border-l-4 border-green-500",
        warning: "bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400 border-l-4 border-yellow-500",
        error:   "bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border-l-4 border-red-500",
    };

    // ─── Helper ───────────────────────────────────────────────────────────────────
    const showNotification = (message, type = "success") => {
        setNotification({ message, type });
        setTimeout(() => setNotification(null), 3500);
    };

    // ───────────────────────────────────────────────────────────────────────────────
    // 1️⃣  GET — User/get/allUsers
    // ───────────────────────────────────────────────────────────────────────────────
    const fetchUsers = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await axios.get((`${API_BASE}feedback/get/allBccUsers`), {headers:{
                    Authorization: decryptedValue
                }});
            // Pick only id, name, email — ignore createdAt, lastModified etc.
            const cleaned = response.data.map((u) => ({
                id:    u.id,
                name:  u.name,
                email: u.email,
            }));
            setUsers(cleaned);
        } catch (err) {
            console.error("Failed to fetch users:", err);
            setError("Failed to load recipients. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    // ─── Fetch on mount ───────────────────────────────────────────────────────────
    useEffect(() => {
        fetchUsers();
    }, []);

    // ───────────────────────────────────────────────────────────────────────────────
    // 3️⃣  DELETE — User/delete/user/{id}
    // ───────────────────────────────────────────────────────────────────────────────
    const handleDeleteUser = async (id) => {
        const user = users.find((u) => u.id === id);
        if (!user) {
            showNotification("The selected email entry could not be found. Refresh and try again.", "error");
            return;
        }
        if (!window.confirm(`Are you sure you want to delete "${user.name}"?`)) return;
        try {
            await axios.delete((`${API_BASE}feedback/delete/bccUser/${encodeURIComponent(id)}`),{
                headers:{
                    Authorization: decryptedValue
                },
                data: {id}
                }
            );
            // Remove from local state after successful delete
            setUsers((prev) => prev.filter((u) => u.id !== id));
            sessionStorage.removeItem("bccEmailList");
            showNotification(`${user.name} has been removed successfully.`);
        } catch (err) {
            console.error("Failed to delete user:", err);
            showNotification(err?.response?.data?.message || `Failed to delete ${user.name}. Please try again.`, "error");
        }
    };

    // ───────────────────────────────────────────────────────────────────────────────
    // 2️⃣  POST — User/save/user          → called when adding new recipient
    // 4️⃣  POST — User/update/user/{id}   → called when editing existing recipient
    // ───────────────────────────────────────────────────────────────────────────────
    const handleSaveUser = async (userData) => {
        if (editingUser) {
            // ── 4️⃣ UPDATE existing user ──────────────────────────────────────────────
            try {
                await axios.post(`${API_BASE}feedback/update/bccUser`, {
                    id: editingUser.id,
                    name:  userData.name,
                    email: userData.email,
                }, {headers:{
                    Authorization: decryptedValue
                    }});
                // Update local state after successful update
                setUsers((prev) =>
                    prev.map((u) =>
                        u.id === editingUser.id
                            ? { ...u, name: userData.name, email: userData.email }
                            : u
                    )
                );
                sessionStorage.removeItem("bccEmailList");
                showNotification(`${userData.name}'s details updated successfully!`);
            } catch (err) {
                console.error("Failed to update user:", err);
                showNotification(err?.response?.data?.message || `Failed to update ${userData.name}. Please try again.`, "error");
                return;
            }
        } else {
            // ── 2️⃣ SAVE new user ─────────────────────────────────────────────────────
            try {
                const response = await axios.post(`${API_BASE}feedback/save/bccUser`, {
                    name:  userData.name,
                    email: userData.email,
                }, {headers: {Authorization: decryptedValue}});
                // Use id returned from API, fallback to Date.now() if not returned
                const newUser = {
                    id:    response.data?.id || Date.now(),
                    name:  userData.name,
                    email: userData.email,
                };
                setUsers((prev) => [...prev, newUser]);
                sessionStorage.removeItem("bccEmailList");
                showNotification(`${userData.name} added to BCC list successfully!`);
            } catch (err) {
                console.error("Failed to add user:", err);
                showNotification(err?.response?.data?.message || `Failed to add ${userData.name}. Please try again.`, "error");
                return; // Don't close modal if API failed
            }
        }
        setEditingUser(null);
        setIsModalOpen(false);
    };

    // ─── Modal Handlers ───────────────────────────────────────────────────────────
    const handleEditUser   = (user) => { setEditingUser(user); setIsModalOpen(true);  };
    const handleOpenAdd    = ()     => { setEditingUser(null); setIsModalOpen(true);  };
    const handleCloseModal = ()     => { setIsModalOpen(false); setEditingUser(null); };

    // ─── Render ───────────────────────────────────────────────────────────────────
    return (
        <div className="min-h-screen bg-gray-50 dark:bg-navy-900 text-gray-900 dark:text-white transition-colors duration-300">

            {/* ── Toast ──────────────────────────────────────────────────────── */}
            {notification && (
                <div className={`fixed top-6 right-6 z-[99999] flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium shadow-lg min-w-[260px] max-w-[380px] ${toastClass[notification.type]}`}>
                    <span>{notification.message}</span>
                    <button
                        onClick={() => setNotification(null)}
                        className="ml-auto opacity-60 hover:opacity-100 bg-transparent border-none cursor-pointer text-inherit"
                    >
                        ✕
                    </button>
                </div>
            )}

            {/* ── Header ─────────────────────────────────────────────────────── */}
            <div className="bg-white dark:bg-navy-800 px-8 py-5 flex items-center justify-between gap-4 border-b border-gray-200 dark:border-navy-700 flex-wrap transition-colors duration-300">
                <div className="flex flex-col gap-1">
                    <h1 className="text-xl font-bold text-gray-900 dark:text-white m-0">
                        Communication Emails
                    </h1>
                </div>
                <button
                    onClick={handleOpenAdd}
                    style={{ background: "#004c86" }}
                    onMouseEnter={(e) => e.currentTarget.style.background = "#003d6b"}
                    onMouseLeave={(e) => e.currentTarget.style.background = "#004c86"}
                    className="text-white px-4 py-2 rounded-lg text-sm font-semibold cursor-pointer border-none transition-all duration-200 hover:-translate-y-px flex items-center gap-2 whitespace-nowrap"
                >
                    + Add Recipient
                </button>
            </div>


            {/* ── Table Card ─────────────────────────────────────────────────── */}
            <div className="bg-white dark:bg-navy-800 rounded-xl shadow-sm mx-8 mt-6 mb-8 border border-gray-200 dark:border-navy-700 overflow-hidden transition-colors duration-300">

                {/* Table Card Header */}
                <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 dark:border-navy-700">
                    {/* Retry button — shows only on error */}
                    {error && (
                        <button
                            onClick={fetchUsers}
                            className="text-xs text-indigo-600 dark:text-indigo-400 font-semibold hover:underline bg-transparent border-none cursor-pointer"
                        >
                            🔄 Retry
                        </button>
                    )}
                </div>

                {/* ── Loading State ───────────────────────────────────────────── */}
                {isLoading && (
                    <div className="flex flex-col items-center justify-center py-20 gap-3 text-gray-400 dark:text-gray-500">
                        <div className="w-8 h-8 border-4 border-gray-200 dark:border-navy-600 border-t-indigo-500 rounded-full animate-spin" />
                        <p className="text-sm font-medium">Loading recipients...</p>
                    </div>
                )}

                {/* ── Error State ─────────────────────────────────────────────── */}
                {!isLoading && error && (
                    <div className="flex flex-col items-center justify-center py-20 gap-3 text-red-500 dark:text-red-400">
                        <div className="text-5xl">⚠️</div>
                        <p className="text-sm font-medium">{error}</p>
                        <button
                            onClick={fetchUsers}
                            className="mt-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold rounded-lg border-none cursor-pointer transition-all duration-200"
                        >
                            Try Again
                        </button>
                    </div>
                )}

                {/* ── Table ───────────────────────────────────────────────────── */}
                {!isLoading && !error && (
                    <UserTable
                        users={users}
                        onEdit={handleEditUser}
                        onDelete={handleDeleteUser}
                    />
                )}
            </div>

            {/* ── Modal ──────────────────────────────────────────────────────── */}
            {isModalOpen && (
                <AddUserModal
                    user={editingUser}
                    onSave={handleSaveUser}
                    onClose={handleCloseModal}
                />
            )}
        </div>
    );
}
